import { XIcon } from '@heroicons/react/outline';
import { useFormik } from 'formik';
import { useContext, useState } from 'react';
import { isMobile } from 'react-device-detect';

import { sendEmail } from 'api/sendEmail.api';
import Email from 'assets/icons/email.png';
import Phone from 'assets/icons/phone.png';
import Input from 'components/Input';
import TextArea from 'components/TextArea';
import { config } from 'config';
import { ContactModalContext } from 'context/contactModalContext';
import { TranslationContext } from 'context/translationContext';
import { ContactSchema } from 'utils/validation';

import styles from './styles.module.scss';
import { Props } from './types';

const ContactModal = ({ message }: Props) => {
  const { closeModal } = useContext(ContactModalContext);
  const { t } = useContext(TranslationContext);
  const [sendMessage, setSendMessage] = useState<string>('');
  const [submitDisabled, setSubmitDisabled] = useState<boolean>(false);

  const handleSubmit = () => {
    if (submitDisabled) return;

    const { email, phone, message } = formik.values;

    sendEmail(email, phone, message)
      .then(() => {
        setSendMessage(t('sendMessageSuccess'));
        setSubmitDisabled(true);
      })
      .catch(() => setSendMessage(t('sendMessageError')));
  };

  const formik = useFormik({
    initialValues: {
      email: '',
      phone: '',
      message,
      termsAccepted: false,
    },
    onSubmit: () => handleSubmit(),
    validationSchema: ContactSchema(t),
  });

  const changeAcceptTermsValue = () => {
    formik.setFieldValue('termsAccepted', !formik.values.termsAccepted);
  };

  return (
    <div className={styles.container}>
      <div className={styles.wrapper}>
        <div className={styles.closeBtnWrapper} onClick={closeModal}>
          <XIcon className={styles.closeBtn} />
        </div>
        <h2 className={styles.header}>{t('askForOffer')}</h2>
        <div className={styles.firstRow}>
          <Input
            label={t('mail')}
            value={formik.values.email}
            placeholder={isMobile ? t('eMailAddress') : t('yourEMailAddress')}
            onChange={formik.handleChange('email')}
            error={formik.touched.email ? formik.errors.email : ''}
            icon={Email}
          />
          <Input
            label={t('phone')}
            value={formik.values.phone}
            placeholder={isMobile ? t('phoneNumber') : t('yourPhoneNumber')}
            onChange={formik.handleChange('phone')}
            error={formik.touched.phone ? formik.errors.phone : ''}
            icon={Phone}
          />
        </div>
        <TextArea
          label={t('message')}
          value={formik.values.message}
          placeholder={t('yourMessage')}
          onChange={formik.handleChange('message')}
          error={formik.touched.message ? formik.errors.message : ''}
        />
        <div className={styles.termsWrapper}>
          <div
            className={styles.termsCheckboxContainer}
            onClick={changeAcceptTermsValue}
          >
            {formik.values.termsAccepted ? (
              <div className={styles.checkedTermsCheckbox} />
            ) : null}
          </div>
          <span className={styles.label}>{t('acceptTerms')}</span>
        </div>
        <span className={styles.termsError}>
          {formik.touched.termsAccepted ? formik.errors.termsAccepted : ''}
        </span>
        {/* @ts-ignore */}
        <button className={styles.btn} onClick={formik.handleSubmit}>
          {t('askForOffer')}
        </button>
        <p className={styles.orCall}>{t('orCall')}</p>
        <a href={`tel:${config.phone}`} className={styles.callBtn}>
          {config.phone}
        </a>
        <span className={styles.sendMessage}>{sendMessage}</span>
      </div>
      <div className={styles.overlay} onClick={closeModal} />
    </div>
  );
};

export default ContactModal;
